import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Spin, Steps, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useMyNodes } from '../../hooks/use_my_nodes';
import { useNodeSignup, useSlugAvailability } from '../../hooks/use_node_signup';
import { suggestSlug } from '../../utils/node_slug';

const { Title, Paragraph, Text } = Typography;

// The job's steps, in order (backend services/node_signups.py).
const STEPS = [
    { key: 'registry', title: 'Registered' },
    { key: 'identity', title: 'Sign-in set up' },
    { key: 'owner', title: 'You, as its first member' },
    { key: 'activate', title: 'Live' },
];

const REASONS = {
    invalid: 'Lowercase letters, digits and hyphens only; it cannot start or end with a hyphen.',
    reserved: 'That label is reserved.',
    taken: 'That label is taken.',
};

function stepStatus(signup, key) {
    const value = signup.steps?.[key];
    if (value === 'done') return 'finish';
    if (typeof value === 'string' && value.startsWith('failed')) return 'error';
    return signup.status === 'failed' ? 'wait' : 'process';
}

// Older backends send neither field: fall back to what the status implies.
const retryable = signup => signup.can_retry ?? ['failed', 'identity_pending', 'pending'].includes(signup.status);

function identityReason(signup) {
    if (signup.identity_error) return signup.identity_error;
    const value = signup.steps?.identity;
    return typeof value === 'string' && value.startsWith('failed') ? value.replace(/^failed:\s*/, '') : null;
}

function Progress({ signup, busy, onRetry, onRefresh }) {
    const items = STEPS.map(step => ({
        title: step.title,
        status: stepStatus(signup, step.key),
        ...(stepStatus(signup, step.key) === 'error' && step.key === 'identity'
            ? { description: 'Not set up yet' } : {}),
    }));
    const reason = identityReason(signup);
    const retryButton = <Button onClick={onRetry} loading={busy} disabled={!retryable(signup)}>Retry</Button>;
    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Steps size="small" items={items} />
            {signup.status === 'ready' && (
                <Alert
                    type="success"
                    showIcon
                    message={`${signup.name} is ready`}
                    description={<>Your node lives at <Text code>{signup.hostname}</Text>. You are its first member and its superadmin.</>}
                    action={<Button type="primary" href={signup.url}>Open {signup.hostname}</Button>}
                />
            )}
            {signup.status === 'identity_pending' && (
                <Alert
                    type="warning"
                    showIcon
                    message={`${signup.name} is live, but signing in at ${signup.hostname} is not set up yet`}
                    description={
                        <Space direction="vertical" size="small">
                            <span>
                                Your node, its address and your membership are all in place. What is missing is
                                registering <Text code>https://{signup.hostname}</Text> with the sign-in service, so
                                opening it now ends on a sign-in error rather than your node.
                            </span>
                            {reason && <span>Why: <Text type="secondary">{reason}</Text></span>}
                            <span>
                                This needs the people who run this Vellby deployment: the sign-in service has to let
                                Vellby add new addresses. Once they have, press Retry; nothing already done is repeated,
                                and you can come back to this page at any time to do it.
                            </span>
                        </Space>
                    }
                    action={retryButton}
                />
            )}
            {(signup.status === 'provisioning' || signup.status === 'pending') && (
                <Alert
                    type="info"
                    showIcon
                    message={`${signup.name} is still being set up`}
                    description={retryable(signup)
                        ? 'Setting it up was interrupted before it finished. Retry picks up where it stopped.'
                        : 'This normally takes a few seconds. If it has not finished in a few minutes, Retry picks up where it stopped.'}
                    action={retryable(signup) ? retryButton : <Button onClick={onRefresh} loading={busy}>Check again</Button>}
                />
            )}
            {signup.status === 'failed' && (
                <Alert
                    type="error"
                    showIcon
                    message="Something went wrong"
                    description={signup.error || 'The node could not be set up.'}
                    action={retryButton}
                />
            )}
        </Space>
    );
}

/**
 * /nodes/new — create a node of your own (docs/self-service-node-creation-todo.md).
 * Name it, pick (or accept) its address, and the backend makes it, puts you
 * inside as its first member and superadmin, and hands you the link. Served
 * by the platform node with self-service on; elsewhere the page points at the
 * platform node. ?name= pre-fills the form, for a landing page to link here.
 */
function NewNodePage() {
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const { ready, featureSelfServiceNodes, nodeIsPlatform } = useNodeInfo();
    const { platform } = useMyNodes();
    const { check, result, checking } = useSlugAvailability();
    const { signup, create, retry, refresh, busy, error, reset } = useNodeSignup();
    const [slugEdited, setSlugEdited] = useState(false);

    const canCreateHere = featureSelfServiceNodes && nodeIsPlatform;

    useEffect(() => {
        const name = searchParams.get('name');
        if (name && !form.getFieldValue('name')) {
            form.setFieldsValue({ name, slug: suggestSlug(name) });
            check(suggestSlug(name));
        }
    }, [searchParams, form, check]);

    if (!ready) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

    if (!canCreateHere) {
        const platformUrl = platform?.url ? `${platform.url}/nodes/new` : null;
        return (
            <Card style={{ maxWidth: 640, margin: '24px auto' }}>
                <Title level={3}>New node</Title>
                {platformUrl
                    ? <Alert type="info" showIcon message="Nodes are created from the platform node"
                        action={<Button type="primary" href={platformUrl}>Continue there</Button>} />
                    : <Alert type="info" showIcon message="Creating nodes is not available here" />}
            </Card>
        );
    }

    const onValuesChange = (changed) => {
        if ('name' in changed && !slugEdited) {
            const slug = suggestSlug(changed.name);
            form.setFieldsValue({ slug });
            check(slug);
        }
        if ('slug' in changed) {
            setSlugEdited(true);
            check((changed.slug || '').trim().toLowerCase());
        }
    };

    const useSuggestion = () => {
        if (!result?.suggestion) return;
        form.setFieldsValue({ slug: result.suggestion });
        setSlugEdited(true);
        check(result.suggestion);
    };

    const slug = form.getFieldValue('slug');
    const slugStatus = !slug ? undefined : checking ? 'validating' : result?.slug === slug ? (result.available ? 'success' : 'error') : undefined;
    const slugHelp = slugStatus === 'error'
        ? <><span>{REASONS[result.reason] || 'Not available.'}</span>{result.suggestion && <> Try <Button type="link" size="small" style={{ padding: 0 }} onClick={useSuggestion}>{result.suggestion}</Button>.</>}</>
        : result?.hostname && result.slug === slug ? <>Your node's address: <Text code>https://{result.hostname}</Text></> : ' ';

    const submit = (values) => create({ name: values.name.trim(), slug: values.slug.trim().toLowerCase() }).catch(() => {});

    return (
        <Card style={{ maxWidth: 640, margin: '24px auto' }}>
            <Title level={3}>New node</Title>
            {signup ? (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Progress signup={signup} busy={busy} onRetry={() => retry().catch(() => {})} onRefresh={() => refresh().catch(() => {})} />
                    {error && <Alert type="error" showIcon message={error.message} />}
                    {signup.status !== 'provisioning' && <Button type="link" onClick={() => { reset(); form.resetFields(); setSlugEdited(false); }}>Create another</Button>}
                </Space>
            ) : (
                <>
                    <Paragraph type="secondary">
                        A node is a place of its own, on its own address, with you as its first member and superadmin.
                        Everyone you invite signs in with the same account they use here.
                    </Paragraph>
                    {error && <Alert type="error" showIcon message={error.message} style={{ marginBottom: 16 }} />}
                    <Form form={form} layout="vertical" onFinish={submit} onValuesChange={onValuesChange} requiredMark={false}>
                        <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Give the node a name' }]}>
                            <Input placeholder="Acme" maxLength={255} autoFocus />
                        </Form.Item>
                        <Form.Item
                            name="slug"
                            label="Address"
                            validateStatus={slugStatus}
                            hasFeedback
                            help={slugHelp}
                            rules={[{ required: true, message: 'Choose an address' }]}
                        >
                            <Input placeholder="acme" maxLength={64} />
                        </Form.Item>
                        <Form.Item style={{ marginTop: 8 }}>
                            <Button type="primary" htmlType="submit" loading={busy} disabled={checking || (result && result.slug === slug && !result.available)}>
                                Create node
                            </Button>
                        </Form.Item>
                    </Form>
                </>
            )}
        </Card>
    );
}

export default NewNodePage;
